import { Box, Skeleton } from '@mui/material';

import { MarketCardSkeleton } from '../../common/MarketCardSkeleton';

// Pre-computed arrays to avoid Array.from in JSX
const CHIP_SKELETON_WIDTHS = [75, 90, 80, 65, 85, 70, 95, 75, 80, 85];
const CARD_SKELETON_ITEMS = [1, 2, 3, 4, 5, 6, 7, 8];

// Extracted components to avoid anonymous functions in JSX
const ChipSkeletonItem = ({ width }: { width: number }) => (
  <Skeleton variant="rounded" width={width} height={28} sx={{ borderRadius: 4, flexShrink: 0 }} />
);

interface HomeSkeletonProps {
  /**
   * Grid template for the markets grid below — must mirror whichever real
   * content grid mounts next. Defaults to the fixed-breakpoint grid used by
   * HomeMarketsDisplay (Finance/CryptoView/CryptoFinanceView/New); pages that
   * render HomeComponent's auto-fit grid instead (e.g. [category]/page.tsx)
   * should pass HomeContent's GRID_TEMPLATE_COLUMNS so the loading and
   * loaded grids don't visibly jump between two different shapes.
   */
  gridTemplateColumns?: string | { xs?: string; sm?: string; md?: string; lg?: string };
}

export const HomeSkeleton = ({
  gridTemplateColumns = {
    xs: '1fr',
    sm: 'repeat(2, 1fr)',
    md: 'repeat(3, 1fr)',
    lg: 'repeat(4, 1fr)',
  },
}: HomeSkeletonProps = {}) => {
  return (
    <Box sx={{ py: 2 }}>
      {/* Sticky Search & Filters skeleton (mirrors SearchAndFilters layout) */}
      <Box
        sx={{
          mb: 2,
        }}
      >
        {/* Search / category chips / icons row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
          }}
        >
          {/* Category chips skeleton */}
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              gap: 1,
              borderRight: '1px solid',
              borderColor: 'divider',
              px: 2,
              flex: 1,
            }}
          >
            {CHIP_SKELETON_WIDTHS.map((width, i) => (
              <ChipSkeletonItem key={i} width={width} />
            ))}
          </Box>

          {/* Filter icon skeleton */}
          <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: '10px' }} />

          {/* Bookmarks icon skeleton */}
          <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: '10px' }} />
        </Box>

        {/* Filters panel skeleton (sort / frequency / status) */}
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Skeleton variant="rounded" width={160} height={40} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={140} height={40} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={140} height={40} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>
      </Box>

      {/* Markets Grid Skeleton (mirrors whichever real grid mounts next) */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns,
        }}
      >
        {CARD_SKELETON_ITEMS.map((item) => (
          <MarketCardSkeleton key={item} />
        ))}
      </Box>
    </Box>
  );
};

export default HomeSkeleton;
